import { load, type CheerioAPI } from 'cheerio'
import type { AnyNode } from 'domhandler'
import regEx from 'utils/global/regEx'
import { extractInlineStyles } from './extractInlineStyles'
import {
  canRemoveSeparator,
  isBlankInkText,
  isGluedAroundHidden,
} from './namePartSeparators'
import type { Environment } from 'types/editor'

type SanitizeDynamicTextProps = {
  html: string
  environment: Environment
}

const isElementHidden = (element: AnyNode, $: CheerioAPI): boolean => {
  const style = $(element).attr('style') || ''
  const { styles } = extractInlineStyles(style, ['display'])
  return styles.display === 'none'
}

const isElementOrAncestorHidden = (
  element: AnyNode,
  $: CheerioAPI,
  rootElement: AnyNode,
): boolean => {
  let currentElement = element

  const shouldContinueTraversal = (
    currentElement: AnyNode,
    rootElement: AnyNode,
  ) => {
    const elementExists = currentElement
    const hasNotReachedRoot = currentElement !== rootElement
    const canTraverseUpward = elementExists && hasNotReachedRoot
    return canTraverseUpward
  }

  // Walk up the DOM tree until we reach the root element or document
  while (shouldContinueTraversal(currentElement, rootElement)) {
    if (isElementHidden(currentElement, $)) {
      return true
    }
    currentElement = $(currentElement).parent()[0]
  }

  return false
}

const getVisibleText = (element: AnyNode, $: CheerioAPI): string => {
  let visibleText = ''
  // Check direct text nodes of this element
  $(element)
    .contents()
    .each((_i, node) => {
      const isTextNode = node.type === 'text'
      const isElementNode = node.type === 'tag'
      const isHidden = isElementOrAncestorHidden(node, $, element)
      const shouldRecursivelyGetVisibleText = isElementNode && !isHidden

      if (isTextNode) {
        visibleText += $(node).text()
      } else if (shouldRecursivelyGetVisibleText) {
        visibleText += getVisibleText(node, $)
      }
    })

  return visibleText
}

/**
 * Removes comma-only content from elements while preserving spacing where appropriate
 * @param $ - Cheerio instance
 */
const removeCommaOnlyContent = ($: CheerioAPI): void => {
  const elementsToEmpty = 'p, div'
  const caseDetailElements = '.mceNonEditable[data-case-detail]'

  $(elementsToEmpty).each((_i, el) => {
    const visibleText = getVisibleText(el, $)?.trim?.() || ''
    const hasOnlyCommasAndWhitespace =
      regEx.onlyCommasAndWhitespace.test(visibleText)

    if (hasOnlyCommasAndWhitespace) {
      $(el).empty()
      return
    }

    // check for standalone commas in mceNonEditable children, preserve spacing if needed
    $(el)
      .find(caseDetailElements)
      .each((_j, child) => {
        // Skip hidden elements
        if (isElementOrAncestorHidden(child, $, el)) {
          return
        }

        const childText = $(child).text()
        const hasOnlyCommasAndWhitespace =
          regEx.onlyCommasAndWhitespace.test(childText)

        if (hasOnlyCommasAndWhitespace) {
          // Preserve spacing: if original text had leading/trailing whitespace,
          // replace with single space instead of completely emptying
          const hasLeadingWhitespace =
            childText?.length > childText?.trim()?.length &&
            regEx.comprehensiveWhitespace.test(childText?.charAt?.(0))
          const hasTrailingWhitespace =
            childText?.length > childText?.trim()?.length &&
            regEx.comprehensiveWhitespace.test(
              childText?.charAt?.(childText?.length - 1),
            )
          const shouldPreserveSpacing =
            hasLeadingWhitespace || hasTrailingWhitespace

          if (shouldPreserveSpacing) {
            $(child).text(' ')
          } else {
            $(child).empty()
          }
        }
      })
  })
}

/**
 * Removes "New Text Box" placeholder elements
 * @param $ - Cheerio instance
 */
const removeNewTextBoxPlaceholders = ($: CheerioAPI): void => {
  $('*').each((_i, el) => {
    const elementText = $(el).text()?.trim?.() || ''
    const isNewTextBox = elementText === 'New Text Box'
    if (isNewTextBox) {
      $(el).empty()
    }
  })
}

/**
 * Removes orphaned whitespace text nodes adjacent to hidden name part spans.
 * When a name part span has display:none, orphaned space text nodes directly
 * before or after it should be removed to prevent center-alignment shifts.
 *
 * A space is only an orphan when it is not the sole separator between two
 * visible pieces of content: `Vera [hidden middle] Bril` must keep one
 * separator or the visible parts render glued ("VeraBril"). Content that was
 * saved already-glued by earlier versions is repaired with a single space.
 * @param $ - Cheerio instance
 */
const removeOrphanedNamePartSpaces = ($: CheerioAPI): void => {
  const hiddenNameSpans = $(
    '.mceNonEditable[data-case-detail][style*="display"]',
  )

  hiddenNameSpans.each((_i, el) => {
    if (!isElementHidden(el, $)) return

    const node = el as unknown as {
      nextSibling: AnyNode | null
      previousSibling: AnyNode | null
    }

    // Remove trailing whitespace text node
    if (
      node.nextSibling &&
      node.nextSibling.type === 'text' &&
      isBlankInkText($(node.nextSibling).text()) &&
      canRemoveSeparator(node.nextSibling)
    ) {
      $(node.nextSibling).remove()
    }

    // Remove leading whitespace text node
    if (
      node.previousSibling &&
      node.previousSibling.type === 'text' &&
      isBlankInkText($(node.previousSibling).text()) &&
      canRemoveSeparator(node.previousSibling)
    ) {
      $(node.previousSibling).remove()
    }

    // Repair name parts saved glued by versions that removed both sides.
    // Bare space is safe here: sanitize output feeds preview/PDF rendering
    // and never re-enters a TinyMCE editing session.
    if (isGluedAroundHidden(el)) {
      $(el).before(' ')
    }
  })
}

/**
 * Sanitizes HTML content by removing comma-only elements and "New Text Box" placeholders
 * - Empties elements containing only commas and whitespace
 * - Empties elements containing only "New Text Box"
 * - Preserves spacing in case detail elements when appropriate
 * - Removes orphaned whitespace next to hidden name part spans
 * - Skips processing of hidden elements
 */
export const sanitizeDynamicText = (
  props: SanitizeDynamicTextProps,
): string => {
  const { html, environment } = props

  const isEditor = environment === 'editor'
  if (isEditor) return html

  const $ = load(html)

  // Remove orphaned whitespace around hidden name part spans
  removeOrphanedNamePartSpaces($)

  // Remove comma-only content while preserving spacing
  removeCommaOnlyContent($)

  // Remove "New Text Box" placeholders
  removeNewTextBoxPlaceholders($)

  return $.html()
}
